import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { skipToken } from "@reduxjs/toolkit/query";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import type { DispatchReportRangeItem } from "@/api/report/_models/DispatchReport";
import { getDispatchReportRange } from "@/api/report/getDispatchReportRange";
import { ReconciliationDispatchGroup } from "./ReconciliationDispatchGroup";
import { ReconciliationDispatchRowTemplate } from "./ReconciliationDispatchRowTemplate";
import { ReconciliationDispatchSummaryCard } from "./ReconciliationDispatchSummaryCard";
import Stack from "@mui/material/Stack";

/**
 * Dispatch reconciliation page: loads the dispatch reports for a date range, then looks up each
 * order's Cin7 shipping status one group at a time and counts the unshipped ones.
 */
export const ReconciliationDispatch = () => {
  const [from, setFrom] = useState<DateTime | null>(DateTime.now().minus({ days: 2 }));
  const [to, setTo] = useState<DateTime | null>(DateTime.now());
  const [cursor, setCursor] = useState(-1);
  const [runId, setRunId] = useState(0);
  const [resolved, setResolved] = useState<Record<string, string | null>>({});

  const range = from && to ? { from: from.toISODate() ?? "", to: to.toISODate() ?? "" } : skipToken;
  const query = getDispatchReportRange.useQuery(range);
  const rows = query.data ?? [];

  const groups = rows.reduce((acc, row) => {
    const existing = acc.get(row.orderReference);
    if (existing) {
      existing.push(row);
    }
    else {
      acc.set(row.orderReference, [row]);
    }
    return acc;
  }, new Map<string, DispatchReportRangeItem[]>());

  const groupEntries = [...groups.entries()];
  const unshippedCount = Object.values(resolved).filter((status) => status === "NOT SHIPPED").length;

  useEffect(() => {
    if (!query.data) {
      setCursor(-1);
      return;
    }
    setResolved({});
    setRunId((id) => id + 1);
    setCursor(0);
  }, [query.data]);

  const handleSettled = useCallback(() => {
    setCursor((current) => current + 1);
  }, []);

  const handleResolved = useCallback((orderReference: string, shippingStatus: string | null) => {
    setResolved((prev) => ({ ...prev, [orderReference]: shippingStatus }));
  }, []);

  let error: string | null = null;
  if (query.isError) {
    error = JSON.stringify(query.error);
  }

  return (
    <Box>
      <Stack direction="row">
        <Typography
          variant="h5"
          sx={{ flex: 1, mb: 2, fontWeight: 700 }}
        >
          Dispatch Reconciliation
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <DatePicker
            label="From"
            value={from}
            onChange={(value) => setFrom(value)}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="To"
            value={to}
            onChange={(value) => setTo(value)}
            slotProps={{ textField: { size: "small" } }}
          />
        </Box>
      </Stack>

      {query.data && !query.isFetching && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <ReconciliationDispatchSummaryCard
            label="Dispatch lines"
            value={rows.length}
          />
          <ReconciliationDispatchSummaryCard
            label="Orders"
            value={groups.size}
          />
          <ReconciliationDispatchSummaryCard
            label="Unshipped"
            value={unshippedCount}
          />
        </Stack>
      )}


      {query.isFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error">
          Failed to load dispatch report: {error}
        </Alert>
      )}

      {query.data && !query.isFetching && (
        <Box
          sx={{
            maxHeight: "calc(100vh - 260px)",
            overflow: "auto",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            fontSize: 14,
          }}
        >
          <ReconciliationDispatchRowTemplate
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              fontWeight: 700,
            }}
          >
            <Box sx={{ gridArea: "consignment" }}>Consignment #</Box>
            <Box sx={{ gridArea: "item" }}>Item</Box>
            <Box sx={{ gridArea: "date" }}>Date</Box>
            <Box sx={{ gridArea: "qty", textAlign: "right" }}>Qty</Box>
            <Box sx={{ gridArea: "carrier" }}>Carrier</Box>
            <Box sx={{ gridArea: "tracking" }}>Tracking #</Box>
            <Box sx={{ gridArea: "source" }}>Source file</Box>
          </ReconciliationDispatchRowTemplate>

          {groupEntries.map(([orderReference, lines], i) => (
            <ReconciliationDispatchGroup
              key={`${runId}-${orderReference}`}
              orderReference={orderReference}
              lines={lines}
              isCurrent={i === cursor}
              onSettled={handleSettled}
              onResolved={handleResolved}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
